import React, { useEffect, useCallback, useState } from 'react';
import { Modal, Flexer, Spacer, Button, Radios, Textarea } from '@ontruck/orc';

import { ReactComponent as UnlockIcon } from 'assets/icons/unlock.svg';

import { UNLOCK_JOURNEY_FOR_AGENTS_REASONS, UNLOCK_FOR_AGENTS_REASONS } from 'modules/log/t-logs';
import { IS_TENANT } from 'tenant/config';

const dontShowAsOptionReason = ['MOVED_BY_RESCUER'];

const reasonsLiteral = { ...UNLOCK_FOR_AGENTS_REASONS, ...UNLOCK_JOURNEY_FOR_AGENTS_REASONS };

const tByModel = {
  shipperOrder: {
    header: 'Set order to manual',
    hint: 'Please state the reason why this order must be handled manually',
    confirm: 'Confirm and set order to manual',
    textarea_placeholder: 'What is the reason?',
    ...reasonsLiteral,
  },
  itinerary: {
    header: 'Set to manual',
    hint: 'Please state the reason why this must be handled manually',
    confirm: 'Confirm and set to manual',
    textarea_placeholder: 'What is the reason?',
    ...reasonsLiteral,
  },
  journey: {
    header: 'Set job to manual',
    hint: 'Please state the reason why this job must be handled manually',
    confirm: 'Confirm and set job to manual',
    textarea_placeholder: 'What is the reason?',
    ...reasonsLiteral,
  },
};

const filterOutOptions = (reasonsToFilter) =>
  Object.keys(reasonsToFilter).filter((reason) => !dontShowAsOptionReason.includes(reason));

const reasons = {
  journey: filterOutOptions(UNLOCK_JOURNEY_FOR_AGENTS_REASONS),
  shipperOrder: filterOutOptions(UNLOCK_FOR_AGENTS_REASONS),
  itinerary: filterOutOptions(UNLOCK_JOURNEY_FOR_AGENTS_REASONS),
};

const SetToManualModal = ({ opened, onSubmit, onClose, className, model }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [comment, setComment] = useState('');

  const t = tByModel[model];
  const manualReasons = reasons[model];

  const handleChangeReason = useCallback(
    (_, { value }) => {
      setSelectedReason(value);
    },
    [setSelectedReason]
  );

  const handleChangeComment = useCallback(
    (_, { value }) => {
      setComment(value);
    },
    [setComment]
  );

  const handleSubmit = useCallback(() => {
    onSubmit && onSubmit({ reason: selectedReason, comment });
  }, [onSubmit, selectedReason, comment]);

  useEffect(() => {
    if (!opened) {
      setSelectedReason(null);
      setComment('');
    }
    return () => {
      setSelectedReason(null);
      setComment('');
    };
  }, [opened]);

  useEffect(() => {
    if (opened && IS_TENANT) {
      onSubmit && onSubmit({ reason: 'REASON_OTHER', comment: 'tenant review' });
    }
  }, [opened, onSubmit]);

  const buttonDisabled = !selectedReason || (selectedReason === 'REASON_OTHER' && !comment);

  return (
    opened &&
    !IS_TENANT && (
      <Modal
        show
        onClose={onClose}
        size="M"
        headerComponent={
          <Flexer.Parent alignItems="center">
            <Spacer mRight="XS">
              <UnlockIcon />
            </Spacer>
            <div>{t.header}</div>
          </Flexer.Parent>
        }
      >
        <div className={className}>
          <div>{t.hint}</div>
          <div className="radios">
            <Radios
              size="M"
              name="reason"
              value={selectedReason}
              items={manualReasons.map((reason) => ({
                value: reason,
                label: t[reason] || reason,
              }))}
              onChange={handleChangeReason}
            />
            <div className="textarea">
              <Textarea
                label={t.textarea_placeholder}
                value={comment}
                onChange={handleChangeComment}
              />
            </div>
          </div>
          <div className="button-container">
            <Button disabled={buttonDisabled} onClick={handleSubmit}>
              {t.confirm}
            </Button>
          </div>
        </div>
      </Modal>
    )
  );
};

export default SetToManualModal;
